import type { ReactNode } from 'react';

import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';

interface ConfirmPopoverProps {
  /** Controlled open state — let the caller hold it open while a mutation runs
   *  and close it on success. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Element that anchors + toggles the popover (e.g. a delete icon button). */
  trigger: ReactNode;
  title?: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Confirm button variant — defaults to `destructive`, since an inline confirm
   *  almost always guards a destructive action. */
  confirmVariant?: 'destructive' | 'default';
  /** Pending state on the confirm button; blocks both buttons while true. */
  isPending?: boolean;
  onConfirm: () => void;
  /**
   * Optional third button between Cancel and Confirm for the case where the
   * prompt has a genuine alternative (e.g. "these are drafts — discard instead?").
   * Keep it to one; a popover is not a menu. */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'destructive' | 'outline' | 'default';
    isPending?: boolean;
  };
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  /** `data-testid` root for the popover content; buttons derive `${testId}-confirm-button` etc. */
  testId?: string;
}

/**
 * Lightweight inline confirmation anchored at its trigger — a compact popover
 * with a prompt and Cancel / Confirm buttons — for destructive actions that
 * don't warrant a full-screen modal. Controlled, so the caller can keep it open
 * during the mutation and close it on success. Safe to nest inside a Sheet /
 * Dialog: rendered in that subtree, Radix treats it as a nested layer so
 * interacting with it doesn't dismiss the parent.
 */
export function ConfirmPopover({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'destructive',
  isPending = false,
  onConfirm,
  secondaryAction,
  side = 'bottom',
  align = 'center',
  className,
  testId = 'confirm-popover',
}: ConfirmPopoverProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {/* A third button needs the extra width; the row still wraps rather than clips. */}
      <PopoverContent
        side={side}
        align={align}
        className={cn(secondaryAction ? 'w-80' : 'w-64', 'p-3', className)}
        data-testid={testId}
      >
        <div className="space-y-3">
          {title && <p className="text-foreground text-sm font-semibold">{title}</p>}
          {description && (
            <div className="text-muted-foreground text-xs leading-5">{description}</div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending || secondaryAction?.isPending}
              data-testid={`${testId}-cancel-button`}
              className="cursor-pointer disabled:cursor-not-allowed"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              {cancelLabel}
            </Button>
            {secondaryAction && (
              <Button
                type="button"
                variant={secondaryAction.variant ?? 'outline'}
                size="sm"
                isLoading={secondaryAction.isPending ?? false}
                disabled={isPending}
                data-testid={`${testId}-secondary-button`}
                className="cursor-pointer disabled:cursor-not-allowed"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
            <Button
              type="button"
              variant={confirmVariant}
              size="sm"
              isLoading={isPending}
              disabled={secondaryAction?.isPending}
              data-testid={`${testId}-confirm-button`}
              className="cursor-pointer disabled:cursor-not-allowed"
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
